import React from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { mediaUrl, money } from "@/lib/api";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();
  const remaining = Math.max(0, 75 - subtotal);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="bg-noir border-l border-white/10 text-cream w-full sm:max-w-md flex flex-col p-0"
        data-testid="cart-drawer"
        aria-describedby="cart-drawer-desc"
      >
        <SheetHeader className="px-6 py-5 border-b border-white/10">
          <SheetTitle className="font-serif text-2xl text-cream flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-gold" strokeWidth={1.5} /> Your Bag
          </SheetTitle>
          <span id="cart-drawer-desc" className="sr-only">Items currently in your shopping bag</span>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
            <p className="font-serif text-xl text-white/50">Your bag is empty</p>
            <Link
              to="/shop"
              onClick={() => setOpen(false)}
              className="px-6 py-3 bg-gold text-black font-body uppercase tracking-widest text-xs hover:bg-champagne transition-colors"
              data-testid="empty-cart-shop-btn"
            >
              Explore the Boutique
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {items.map((i) => (
                <div key={i.key} className="flex gap-4" data-testid={`cart-item-${i.key}`}>
                  <div className="w-20 h-24 bg-noir-2 overflow-hidden shrink-0">
                    {i.image && <img src={mediaUrl(i.image)} alt={i.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <h4 className="font-serif text-base text-cream leading-snug">{i.name}</h4>
                      <button onClick={() => removeItem(i.key)} data-testid={`remove-${i.key}`}>
                        <X className="w-4 h-4 text-white/40 hover:text-gold" />
                      </button>
                    </div>
                    {i.variant_label && <p className="font-body text-[11px] uppercase tracking-[0.12em] text-champagne/80 mt-0.5">{i.variant_label}</p>}
                    <p className="font-body text-sm text-gold mt-1">{money(i.price)}</p>
                    <div className="flex items-center gap-3 mt-2 border border-white/15 w-fit">
                      <button onClick={() => updateQty(i.key, i.quantity - 1)} className="px-2 py-1 hover:text-gold" data-testid={`dec-${i.key}`}>
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="font-body text-sm w-4 text-center">{i.quantity}</span>
                      <button onClick={() => updateQty(i.key, i.quantity + 1)} className="px-2 py-1 hover:text-gold" data-testid={`inc-${i.key}`}>
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 px-6 py-5 space-y-4">
              {remaining > 0 ? (
                <p className="font-body text-xs text-champagne/80 text-center">
                  Add {money(remaining)} more for complimentary standard shipping
                </p>
              ) : (
                <p className="font-body text-xs text-gold text-center">You've unlocked complimentary standard shipping</p>
              )}
              <div className="flex justify-between font-body">
                <span className="text-white/60 text-sm uppercase tracking-widest">Subtotal</span>
                <span className="text-cream text-lg" data-testid="cart-subtotal">{money(subtotal)}</span>
              </div>
              <Link
                to="/cart"
                onClick={() => setOpen(false)}
                className="block text-center w-full py-3.5 bg-gold text-black font-body uppercase tracking-widest text-xs hover:bg-champagne transition-colors"
                data-testid="view-cart-btn"
              >
                View Bag & Checkout
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
